import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getOrderById } from "../../services/orderApi";
import {
  addOrderStatus,
  getOrderStatusHistory,
} from "../../services/orderStatusApi";

import API from "../../services/api";

const OrderDetailsPage = () => {
  const { id } = useParams();

  const [order, setOrder] = useState(null);
  const [history, setHistory] = useState([]);

  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [visible, setVisible] = useState(true);

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // =========================
  // LOAD ORDER + HISTORY
  // =========================
  const loadData = async () => {
    try {
      setLoading(true);

      const orderData = await getOrderById(id);
      setOrder(orderData);

      const historyData = await getOrderStatusHistory(id);
      setHistory(historyData || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // =========================
  // UPDATE STATUS
  // =========================
  const handleUpdate = async () => {
    if (!status) return setMsg("❌ Please select status");

    try {
      setLoading(true);
      setMsg("");

      await addOrderStatus(id, {
        status,
        note,
        is_visible_to_user: visible,
      });

      setMsg("✅ Status updated");

      setStatus("");
      setNote("");

      loadData();
    } catch (err) {
      setMsg(err.response?.data?.message || "❌ Failed to update");
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // DOWNLOAD PDF (BACKEND)
  // =========================
  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await API.get(`/orders/${id}/pdf`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `order-${order.order_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.log(err);
      alert("Failed to download PDF");
    }
  };

  // =========================
  // DOWNLOAD EXCEL (BACKEND)
  // =========================
  const downloadExcel = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await API.get(`/orders/${id}/excel`, {
        responseType: "blob",
        headers: { Authorization: `Bearer ${token}` },
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", `order-${order.order_id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.log(err);
      alert("Failed to download Excel");
    }
  };

  if (!order) return <p style={{ padding: 20 }}>Loading order...</p>;

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* HEADER */}
        <div style={styles.header}>
          <h2>📦 Order Details Panel</h2>

          <div style={{ display: "flex", gap: "10px" }}>
            <button style={styles.downloadBtn} onClick={downloadPDF}>
              📄 PDF
            </button>

            <button style={styles.excelBtn} onClick={downloadExcel}>
              📊 Excel
            </button>
          </div>
        </div>

        {/* ORDER CARD */}
        <div style={styles.card}>
          <div style={styles.grid}>
            <p><b>Order ID:</b> {order.order_id}</p>
            <p><b>Name:</b> {order.user_name}</p>
            <p><b>Phone:</b> {order.user_phone}</p>
            <p>
              <b>Status:</b>{" "}
              <span style={styles.badge(order.status)}>
                {order.status}
              </span>
            </p>
            <p><b>Payment:</b> {order.payment_status}</p>
            <p><b>Total Items:</b> {order.total_items}</p>
            <p><b>Total Quantity:</b> {order.total_quantity}</p>
            <p>
              <b>Time:</b>{" "}
              {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
        </div>

        {/* HISTORY */}
        <div style={styles.card}>
          <h3>📜 Status History</h3>

          {history.length === 0 ? (
            <p>No history found</p>
          ) : (
            <ul style={styles.list}>
              {history.map((h) => (
                <li key={h.id} style={styles.listItem}>
                  <span style={styles.badge(h.status)}>
                    {h.status}
                  </span>

                  <span style={{ marginLeft: 10 }}>
                    {new Date(h.created_at).toLocaleString()}
                  </span>

                  {h.note && (
                    <div style={{ color: "#64748b", marginTop: 4 }}>
                      {h.note}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* UPDATE */}
        <div style={styles.card}>
          <h3>⚙️ Update Order Status</h3>

          <select
            style={styles.input}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="">Select Status</option>
            <option value="processing">Processing</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
          </select>

          <textarea
            style={styles.textarea}
            placeholder="Add note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <label style={styles.checkbox}>
            <input
              type="checkbox"
              checked={visible}
              onChange={(e) => setVisible(e.target.checked)}
            />
            Visible to customer
          </label>

          <button style={styles.button} onClick={handleUpdate}>
            Update Status
          </button>

          {msg && (
            <p style={{ color: msg.includes("✅") ? "green" : "red" }}>
              {msg}
            </p>
          )}
        </div>

      </div>
    </div>
  );
};

export default OrderDetailsPage;

/* ===================== STYLES ===================== */
const styles = {
  page: {
    background: "#f1f5f9",
    minHeight: "100vh",
    padding: "20px",
  },

  container: {
    maxWidth: "900px",
    margin: "auto",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
  },

  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "10px",
    padding: "15px",
    marginBottom: "15px",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
  },

  list: {
    listStyle: "none",
    padding: 0,
  },

  listItem: {
    padding: "10px",
    borderBottom: "1px solid #eee",
  },

  input: {
    width: "100%",
    padding: "8px",
    marginBottom: "10px",
  },

  textarea: {
    width: "100%",
    height: "70px",
    marginBottom: "10px",
  },

  checkbox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "10px",
  },

  button: {
    padding: "10px 15px",
    background: "#1e293b",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
  },

  downloadBtn: {
    padding: "8px 12px",
    background: "#2563eb",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
  },

  excelBtn: {
    padding: "8px 12px",
    background: "#16a34a",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
  },

  badge: (status) => ({
    padding: "3px 8px",
    borderRadius: "6px",
    fontSize: "12px",
    background:
      status === "delivered"
        ? "#dcfce7"
        : status === "shipped"
        ? "#dbeafe"
        : "#fef9c3",
    color:
      status === "delivered"
        ? "#166534"
        : status === "shipped"
        ? "#1e40af"
        : "#92400e",
  }),
};